<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import TwoPanePage from "@/templates/TwoPanePage.vue";
  import EpistoAiConversationList from "@/components/App/Assistant/EpistoAiConversationList.vue";
  import AssistantHeader from "@/components/App/Assistant/AssistantHeader.vue";
  import AssistantThread from "@/components/App/Assistant/AssistantThread.vue";
  import { useAssistantStore, type AssistantAnswer } from "@/stores/assistant";
  import { useAssistantScope } from "@/composables/useAssistantScope";
  import { useCurrentUserStore } from "@/stores/currentUser";

  const store = useAssistantStore();
  const {
    conversations,
    currentConversationToken,
    currentTitle,
    messages,
    isStreaming,
    streamingContent,
    streamingSteps,
    streamingSummary,
    scopeDefinition,
    suggestionGroups,
    lastTurn,
  } = storeToRefs(store);
  const { getUser } = storeToRefs(useCurrentUserStore());

  const { scope } = useAssistantScope();
  const instance = getCurrentInstance();
  const thread = ref<InstanceType<typeof AssistantThread> | null>(null);
  const route = useRoute();
  const router = useRouter();

  store.loadScopeDefinitions();

  // Binding the scope here rather than leaving it to AssistantHost, which is
  // set up after this page and would otherwise drop the restored thread.
  store.bindScope(scope.value);

  // The URL is the thread being read, both ways: opening one writes its token,
  // and landing on a token reopens it.
  watch(
    () => route.params.token as string | undefined,
    async (token) => {
      if (!token || token === currentConversationToken.value) return;
      if (!(await store.openConversation(token))) router.replace({ name: "EpistoAi", params: {} });
    },
    { immediate: true },
  );

  watch(currentConversationToken, (token) => {
    if ((route.params.token ?? "") === (token ?? "")) return;

    router.replace({ name: "EpistoAi", params: { token: token ?? "" } });
  });

  const scopeHint = computed(() => {
    if (!scopeDefinition.value) return null;

    return { verb: scopeDefinition.value.verb, target: scopeDefinition.value.target, filters: 0 };
  });

  function startConversation() {
    store.newConversation();
    thread.value?.focusComposer();
  }

  async function selectConversation(token: string) {
    await store.openConversation(token);
    thread.value?.focusComposer();
  }

  function sendMessage(content: string, files: File[] = []) {
    store.sendMessage(content, instance?.proxy?.$cable, instance, files, scope.value);
  }

  function answer(messageId: string, text: string, answers: Record<string, AssistantAnswer>) {
    store.answerQuestion(messageId, text, answers, instance?.proxy?.$cable, instance);
  }
</script>

<template>
  <TwoPanePage
    :span-left="3"
    :span-right="9">
    <template #left>
      <div class="h-[calc(100vh-140px)]">
        <EpistoAiConversationList
          :conversations="conversations"
          :current-conversation-token="currentConversationToken"
          :disabled="isStreaming"
          @new="startConversation"
          @select="selectConversation"
          @remove="store.deleteConversation"
          @rename="store.renameConversation" />
      </div>
    </template>

    <template #right>
      <section class="flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-2xl border border-border bg-background">
        <AssistantHeader
          :working="isStreaming"
          :conversation-title="currentTitle"
          :show-history="false"
          :closable="false"
          @new="startConversation" />

        <div class="mx-auto min-h-0 w-full max-w-3xl flex-1">
          <AssistantThread
            ref="thread"
            :messages="messages"
            :is-streaming="isStreaming"
            :streaming-content="streamingContent"
            :streaming-steps="streamingSteps"
            :streaming-summary="streamingSummary"
            :suggestion-groups="suggestionGroups"
            :scope-hint="scopeHint"
            :first-name="getUser?.first_name ?? ''"
            :can-retry="!!lastTurn"
            @send="sendMessage"
            @stop="store.stopGeneration"
            @retry="store.retryLastTurn"
            @feedback="store.sendFeedback"
            @answer="answer" />
        </div>
      </section>
    </template>
  </TwoPanePage>
</template>
